import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Music2, Sparkles, Users, Heart, Quote, MapPin, Calendar, Clock, Mail, Instagram, Phone, MessageCircle, Bell, ArrowRight, X, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { LotusDivider, Reveal, Eyebrow } from "./Decorative";
import { ROUTES } from "../lib/seo";
import { ABOUT_PHOTO, WHATSAPP_URL, ANTARA_IG, KASHI_IG, KASHI_SITE, IG_REEL_URL, YT_VIDEO_ID, YT_SHORT_ID, YT_TESTIMONIAL_ID, LOGO_URL, CLOSING_PHOTO, LAST_EDITION } from "../lib/constants";
import { trackWhatsAppClick, trackInstagramClick } from "../lib/track";
import { useUpcomingShows, fullDate, timeLines } from "../lib/shows";
import { EVENT_TITLE, PRESENTER, FEATURING } from "../data/shows";
import { EDITIONS, byFile, featured, src as photoSrc, srcSet as photoSrcSet, webpSrcSet } from "../lib/gallery";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

/* ========== 2. Opening Quote ========== */
export function OpeningQuote() {
  return (
    <section className="bg-ivory py-28 md:py-40 px-6" data-testid="opening-quote-section">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <Quote className="mx-auto text-gold mb-6" size={28} strokeWidth={1} />
          <p className="font-display italic text-3xl sm:text-4xl md:text-5xl lg:text-[52px] leading-[1.25] gold-grad">
            Where sound is not just heard…<br />but felt.
          </p>
          <p className="mt-8 font-body text-sm tracking-luxe uppercase text-amber/80">— Antara</p>
        </Reveal>
        <LotusDivider />
      </div>
    </section>
  );
}

/* ========== 3. About Antara ========== */
const PILLARS = [
  { icon: Music2, label: "FORMAT", desc: "A live, immersive bhajan experience" },
  { icon: Sparkles, label: "SOUND", desc: "Sacred music reimagined with contemporary production" },
  { icon: Users, label: "POSTURE", desc: "No stage hierarchy — collective participation" },
  { icon: Heart, label: "INTENTION", desc: "A space where people don't just listen — they feel, connect, and transform" },
];

export function AboutAntara() {
  return (
    <section className="bg-ivory-2 py-24 md:py-36 px-6 relative" data-testid="about-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-[1.1fr_0.9fr] gap-16 items-center">
        <div>
          <Reveal>
            <Eyebrow>About Antara</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-[56px] leading-[1.05] text-ink">
              Not a concert.<br /><span className="gold-grad">An experience.</span>
            </h2>
            <p className="mt-6 font-serif-italic text-xl md:text-2xl text-amber leading-relaxed max-w-xl">
              Antara is a new-age spiritual music format where the boundary between artist and audience dissolves.
            </p>
            <p className="mt-4 font-body text-base text-ink-2/85 leading-relaxed max-w-xl">
              This is not about watching. This is about being part of something.
            </p>
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.1}>
                <div className="card-lift p-6 rounded-2xl border border-gold/25 bg-ivory/60 backdrop-blur-sm" data-testid={`pillar-${p.label.toLowerCase()}`}>
                  <p.icon className="text-gold mb-4" size={24} strokeWidth={1.2} />
                  <h3 className="font-display text-lg tracking-mid uppercase text-ink">{p.label}</h3>
                  <p className="mt-2 font-body text-sm text-ink-2/80 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.2}>
          <div className="relative">
            <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-gold/30 via-amber/15 to-transparent blur-xl" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] border border-gold/30 shadow-[0_30px_80px_-20px_rgba(184,118,62,0.4)] bg-gradient-to-br from-[#FBF6EE] to-[#E8D9C5]">
              <img src={ABOUT_PHOTO} alt="Antara curators — Kashi with Anshu" width="800" height="1000" loading="lazy" decoding="async" className="w-full h-full object-contain" />
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <p className="mt-20 text-center font-serif-italic text-lg md:text-xl text-amber max-w-2xl mx-auto">
          Inspired by the energy of Kashi, designed for modern India.
        </p>
      </Reveal>
    </section>
  );
}

/* ========== 4. Emotional Arc ========== */
const STAGES = [
  { n: "01", name: "Arrival", desc: "A transition from chaos to calm" },
  { n: "02", name: "Grounding", desc: "Soft, meditative bhajans align the room" },
  { n: "03", name: "Elevation", desc: "Collective singing builds energy and unity" },
  { n: "04", name: "Stillness", desc: "A closing moment that lingers beyond the event" },
];

export function EmotionalArc() {
  return (
    <section className="bg-ivory py-24 md:py-36 px-6 relative overflow-hidden" data-testid="arc-section">
      <div className="max-w-6xl mx-auto text-center">
        <Reveal>
          <Eyebrow>The Experience</Eyebrow>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">An emotional arc, not a lineup</h2>
          <p className="mt-5 font-body text-base md:text-lg text-ink-2/80 max-w-2xl mx-auto">
            Antara is crafted like a journey inward.
          </p>
        </Reveal>

        {/* Wave ribbon */}
        <div className="relative mt-20 hidden md:block" data-testid="arc-wave-desktop">
          <svg viewBox="0 0 1200 200" className="w-full h-44" preserveAspectRatio="none" aria-hidden>
            <defs>
              <linearGradient id="ribbon" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#C9A961" />
                <stop offset="40%" stopColor="#B8763E" />
                <stop offset="80%" stopColor="#E8D9C5" />
                <stop offset="100%" stopColor="#C9A961" />
              </linearGradient>
            </defs>
            <path d="M0,100 C 200,30 400,170 600,100 C 800,30 1000,170 1200,100" stroke="url(#ribbon)" strokeWidth="2.5" fill="none" />
            <path d="M0,100 C 200,30 400,170 600,100 C 800,30 1000,170 1200,100" stroke="url(#ribbon)" strokeWidth="14" fill="none" opacity="0.18" />
          </svg>
          <div className="absolute inset-0 grid grid-cols-4">
            {STAGES.map((s, i) => {
              const yOffset = i % 2 === 0 ? -36 : 36;
              return (
                <Reveal key={s.n} delay={i * 0.15}>
                  <div className="flex flex-col items-center" style={{ transform: `translateY(${yOffset}px)` }}>
                    <div className="w-20 h-20 rounded-full bg-ivory border border-gold/40 flex items-center justify-center shadow-[0_10px_30px_-12px_rgba(184,118,62,0.35)] backdrop-blur">
                      <span className="font-display text-xl gold-grad">{s.n}</span>
                    </div>
                    <h3 className="mt-4 font-display text-2xl text-ink">{s.name}</h3>
                    <p className="mt-1 font-body text-sm text-ink-2/75 max-w-[200px]">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="md:hidden mt-12 space-y-8 relative">
          <div className="absolute left-10 top-2 bottom-2 w-px bg-gradient-to-b from-gold/60 via-amber/40 to-blush" />
          {STAGES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="flex gap-5 items-start text-left">
                <div className="relative w-20 h-20 flex-shrink-0 rounded-full bg-ivory border border-gold/40 flex items-center justify-center">
                  <span className="font-display text-xl gold-grad">{s.n}</span>
                </div>
                <div className="pt-2">
                  <h3 className="font-display text-2xl text-ink">{s.name}</h3>
                  <p className="mt-1 font-body text-sm text-ink-2/80">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-24 font-display text-3xl md:text-4xl gold-grad italic">Come sing. Come home.</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ========== 2.5. Watch Antara — landscape intro video ========== */
export function WatchAntara() {
  return (
    <section className="bg-ivory-2 py-24 md:py-32 px-6" data-testid="watch-antara-section">
      <div className="max-w-5xl mx-auto text-center">
        <Reveal>
          <Eyebrow>Watch Antara</Eyebrow>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">A glimpse into the experience</h2>
          <p className="mt-5 font-serif-italic text-lg text-amber max-w-xl mx-auto">An evening, distilled into a few quiet minutes.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 mx-auto aspect-video rounded-2xl overflow-hidden border border-gold/30 shadow-[0_30px_80px_-20px_rgba(184,118,62,0.4)]">
            <iframe
              src={`https://www.youtube.com/embed/${YT_VIDEO_ID}`}
              title="Antara — A Glimpse"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-iframe-intro"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ========== 6. See & Feel — YouTube short ========== */
export function InstagramEmbed() {
  return (
    <section className="bg-ivory py-24 md:py-32 px-6" data-testid="instagram-section">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <Eyebrow>See & Feel</Eyebrow>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">See & feel it for yourself</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 mx-auto aspect-[9/16] w-full max-w-[340px] rounded-2xl overflow-hidden border border-gold/30 shadow-[0_30px_80px_-20px_rgba(184,118,62,0.4)]">
            <iframe
              src={`https://www.youtube.com/embed/${YT_SHORT_ID}`}
              title="Antara — feel-it teaser"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-short-iframe"
            />
          </div>
          <p className="mt-6 font-serif-italic text-amber">A felt moment from Antara — sound, light, and stillness.</p>
          <a href={ANTARA_IG} target="_blank" rel="noopener noreferrer" onClick={trackInstagramClick} className="mt-3 inline-flex items-center gap-2 font-body text-sm tracking-wide text-ink hover:text-gold transition" data-testid="ig-follow-link">
            <Instagram size={14} /> Follow @antara.live on Instagram
          </a>
        </Reveal>
      </div>
    </section>
  );
}

/* ========== 7. Curators — Kashi with Anshu ========== */
export function Curators() {
  return (
    <section className="py-28 md:py-36 px-6 relative" style={{ background: "linear-gradient(180deg, #F2EAD9 0%, #EFE3CE 50%, #F2EAD9 100%)" }} data-testid="curators-section">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="font-deva text-3xl md:text-4xl text-amber tracking-wide">काशी</p>
          <Eyebrow>The Curators</Eyebrow>
          <h2 className="mt-4 font-display text-3xl md:text-4xl text-ink">Kashi with Anshu</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-12 font-serif-italic text-2xl md:text-3xl gold-grad leading-snug">
            "I don't take you to Kashi.<br />I take you closer to yourself."
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mt-10 font-body text-base md:text-lg text-ink-2/85 leading-relaxed max-w-2xl mx-auto">
            <a href={KASHI_SITE} target="_blank" rel="noopener" className="font-medium text-gold underline-offset-4 hover:underline transition" data-testid="kashi-inline-link">Kashi with Anshu</a> is a private, intimate <strong className="font-medium">4-day spiritual immersion in Varanasi</strong>, guided by Anshu Goyal and limited to 10 guests per group. Rare aartis, heritage stays, and live bhajan on the Ganga — designed not as a tour, but as a quiet inner journey.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href={KASHI_IG} target="_blank" rel="noopener noreferrer" className="font-body text-sm flex items-center gap-2 text-ink-2 hover:text-gold transition" data-testid="kashi-ig-link">
              <Instagram size={14} /> @kashiwithanshu
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ========== 8. Voices — testimonial video + quotes ========== */
const TESTIMONIALS = [
  "The experience was truly magical and amazing. People were swaying, dancing — it was a different kind of joy altogether. I could really feel the song.",
  "It was so much fun — my first time experiencing something like this. We could feel Mahadev in each and every lyric, in every beat. I'm really grateful to be here at this moment.",
  "A very, very special and soulful experience. The music was beautiful, the evening was beautiful — everything came together so well.",
  "My wife and I had a great time. We genuinely wish to come again whenever you perform next. Wishing you all the best!",
  "Amazing! बहुत अच्छा लगा — I'd love a chance to experience this again. Honestly, I wish this happened every month, every single day. Such a beautiful experience.",
];

export function Voices({ preview = false }) {
  const quotes = preview ? TESTIMONIALS.slice(0, 3) : TESTIMONIALS;
  return (
    <section className="bg-ivory py-24 md:py-32 px-6" data-testid="voices-section">
      <div className="max-w-5xl mx-auto text-center">
        <Reveal>
          <Eyebrow>Voices</Eyebrow>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">In their words</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 mx-auto aspect-video rounded-2xl overflow-hidden border border-gold/30 shadow-[0_30px_80px_-20px_rgba(184,118,62,0.4)] max-w-3xl">
            <iframe
              src={`https://www.youtube.com/embed/${YT_TESTIMONIAL_ID}`}
              title="Antara testimonial"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-iframe-testimonial"
            />
          </div>
          <p className="mt-6 font-serif-italic text-amber">Real voices from past Antara evenings.</p>
        </Reveal>

        {/* Testimonial pull-quotes */}
        <div className="mt-20 grid md:grid-cols-2 gap-5 md:gap-6 text-left">
          {quotes.map((t, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1}>
              <div
                className="card-lift relative h-full p-7 md:p-9 rounded-2xl bg-ivory-2 border border-gold/25"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="text-gold mb-4 opacity-70" size={22} strokeWidth={1.2} />
                <p className="font-serif-italic text-lg md:text-xl text-ink-2 leading-relaxed">
                  {t}
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <span className="h-px w-10 bg-gradient-to-r from-gold to-transparent" />
                  {/* TODO_CONTENT: replace with real name, designation & city */}
                  <span className="eyebrow text-amber/80">A Guest at Antara</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {preview && (
          <Reveal delay={0.15}>
            <Link to={ROUTES.voices.path} className="mt-12 inline-flex items-center gap-2 font-body text-sm tracking-wide text-amber hover:text-gold transition" data-testid="voices-see-all">
              Read more voices <ArrowRight size={15} />
            </Link>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* ========== 9. Why Attend ========== */
const WHY_ATTEND = [
  { label: "700+ seat auditorium in Civil Lines", icon: "🎭" },
  { label: "Live multi-generational congregation", icon: "🪔" },
  { label: "Premium audio & immersive lighting", icon: "✨" },
  { label: "Curated set list — no two shows alike", icon: "🎶" },
];
export function WhyAttend() {
  const icons = [Users, Heart, Sparkles, Music2];
  return (
    <section className="bg-ivory-2 py-24 md:py-32 px-6" data-testid="why-attend-section">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center">
            <Eyebrow>Why Attend</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">Why Attend</h2>
          </div>
        </Reveal>
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY_ATTEND.map((w, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={w.label} delay={i * 0.08}>
                <div className="card-lift h-full p-7 rounded-2xl bg-ivory border border-gold/25 text-center">
                  <Icon className="mx-auto text-gold mb-4" size={28} strokeWidth={1.2} />
                  <p className="font-display text-lg text-ink leading-snug">{w.label}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ========== 10. Delhi recap ========== */
/**
 * Was "Venue & Date" — a when/where block for a show being sold. The show has
 * happened, so this is now a record of it. The venue detail lives on /delhi/.
 */
export function DelhiRecap() {
  const shot = byFile("delhi-2026-08-017");
  return (
    <section className="bg-ivory py-24 md:py-32 px-6" data-testid="delhi-recap-section">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Eyebrow>The last edition</Eyebrow>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink leading-tight">
            Delhi filled the hall<br />twice in one day.
          </h2>
          <p className="mt-6 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
            On {LAST_EDITION.dateLabel}, Antara played its first Delhi edition at{" "}
            {LAST_EDITION.venue} in Civil Lines — a matinee and an evening show, around
            1,500 people across the day, and a room that sang the refrains back louder
            than the band.
          </p>
          <div className="mt-8 space-y-4 font-body text-sm text-ink-2/80">
            <p className="flex items-center gap-3"><Calendar size={16} className="text-gold flex-shrink-0" /> {LAST_EDITION.dateLabel} · two shows</p>
            <p className="flex items-center gap-3"><MapPin size={16} className="text-gold flex-shrink-0" /> {LAST_EDITION.venue}, {LAST_EDITION.venueStreet}</p>
            <p className="flex items-center gap-3"><Users size={16} className="text-gold flex-shrink-0" /> {LAST_EDITION.capacityPerShow} seats a show, both full</p>
          </div>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to={ROUTES.delhi.path} className="btn-outline" data-testid="recap-delhi-link">
              The Delhi edition <ArrowRight size={15} />
            </Link>
            <Link to={ROUTES.editions.path} className="btn-outline" data-testid="recap-editions-link">
              All editions <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="rounded-2xl overflow-hidden border border-gold/30 shadow-[0_24px_60px_-20px_rgba(184,118,62,0.35)]">
            <picture>
              <source type="image/webp" srcSet={webpSrcSet(shot)} sizes="(min-width: 768px) 50vw, 100vw" />
              <img
                src={photoSrc(shot, 1200)}
                srcSet={photoSrcSet(shot, "jpg")}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt={shot.alt}
                width={shot.width}
                height={shot.height}
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
                style={{ backgroundImage: `url(${shot.lqip})`, backgroundSize: "cover" }}
              />
            </picture>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ========== 13. Gallery with lightbox ========== */
export function Gallery({ preview = false }) {
  const [open, setOpen] = React.useState(-1);
  // Filenames come from src/data/gallery.json only — see lib/gallery.js.
  // The home page preview is one flat grid of featured frames; the gallery
  // page is one block per edition, newest first, each with its own heading.
  // Within an edition the featured frames lead (the full room, the stage, the
  // high points), then the rest in the order they were taken — so the block
  // does not open on the registration desk.
  const editions = preview
    ? []
    : EDITIONS.filter((e) => e.photos.length > 0).map((e) => ({
        ...e,
        photos: [...e.photos.filter((p) => p.featured), ...e.photos.filter((p) => !p.featured)],
      }));
  const images = preview ? featured(8) : editions.flatMap((e) => e.photos.map((p) => ({ ...p, edition: e })));
  let index = 0; // running index into `images`, so the lightbox can step across editions
  const tile = (photo) => {
    const i = index++;
    return (
      <Reveal key={photo.file} delay={(i % 3) * 0.08}>
        <button
          type="button"
          onClick={() => setOpen(i)}
          className="group relative aspect-square w-full overflow-hidden rounded-xl border border-gold/20 cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B8923E]"
          aria-label={`Open photograph: ${photo.alt}`}
          data-testid={`gallery-item-${i}`}
        >
          <picture>
            <source type="image/webp" srcSet={webpSrcSet(photo)} sizes="(min-width: 768px) 25vw, 50vw" />
            <img
              src={photoSrc(photo, 400)}
              srcSet={photoSrcSet(photo, "jpg")}
              sizes="(min-width: 768px) 25vw, 50vw"
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
              loading={i < 4 ? "eager" : "lazy"}
              decoding="async"
              className="w-full h-full object-cover sepia-hover transition-transform duration-700 group-hover:scale-110"
              style={{ backgroundImage: `url(${photo.lqip})`, backgroundSize: "cover" }}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </Reveal>
    );
  };
  const grid = "mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4";
  return (
    <section className="bg-ivory-2 py-24 md:py-32 px-6" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="text-center">
            <Eyebrow>Gallery</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">Glimpses</h2>
            <LotusDivider />
          </div>
        </Reveal>
        {preview ? (
          <div className={`${grid} mt-12`}>{images.map(tile)}</div>
        ) : (
          editions.map((e) => (
            <div key={e.id} className="mt-14 first:mt-12" data-testid={`gallery-edition-${e.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-gold/25 pb-4">
                <h3 className="font-display text-3xl md:text-4xl text-ink">
                  {e.title ? `${e.title} · ${e.cityShort}` : e.label}
                </h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-body text-sm text-ink-2/80">
                  <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-gold" /> {editionDate(e.date)}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-gold" /> {e.venue}</span>
                  {e.soldOut && (
                    <span className="rounded-full px-3 py-1 text-xs font-semibold tracking-[0.14em] uppercase sold-out-badge" data-testid={`gallery-sold-out-${e.id}`}>
                      Sold out
                    </span>
                  )}
                </div>
              </div>
              <div className={grid}>{e.photos.map((p) => tile({ ...p, edition: e }))}</div>
              {ARCHIVES[e.id] && (
                <Link
                  to={ARCHIVES[e.id].to}
                  className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-gold/30 bg-ivory px-5 py-4 transition hover:border-gold"
                  data-testid={`gallery-archive-${e.id}`}
                >
                  <span className="font-body text-ink">
                    <span className="block font-display text-xl md:text-2xl">Were you there? Find yourself.</span>
                    <span className="text-sm text-ink-2/80">{ARCHIVES[e.id].count} more photos from the evening, free to download.</span>
                  </span>
                  <ArrowRight size={20} className="shrink-0 text-amber" aria-hidden="true" />
                </Link>
              )}
            </div>
          ))
        )}
        {preview && (
          <Reveal delay={0.15}>
            <div className="mt-12 text-center">
              <Link to={ROUTES.gallery.path} className="inline-flex items-center gap-2 font-body text-sm tracking-wide text-amber hover:text-gold transition" data-testid="gallery-see-all">
                See the full gallery <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        )}
      </div>
      {open >= 0 && images[open] && (
        <Lightbox images={images} index={open} onChange={setOpen} onClose={() => setOpen(-1)} />
      )}
    </section>
  );
}

// Editions with a "find yourself" page of every other photo. The count is
// written here rather than read from the archive file, so the full list of
// photos stays out of the bundle every page loads.
const ARCHIVES = {
  "chandigarh-2026-10": { to: ROUTES.archiveChandigarh.path, count: 336 },
  "delhi-2026-10": { to: ROUTES.archiveDelhi.path, count: 375 },
};

// "2026-10-23" → "23 October 2026"
function editionDate(iso) {
  return new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", day: "numeric", month: "long", year: "numeric" })
    .format(new Date(`${iso}T12:00:00+05:30`));
}

/**
 * Full-screen photograph, built to feel like being back in the room: a dark
 * stage lit by the photo's own colours (its blurred LQIP glows behind it), the
 * picture as large as the screen allows, and controls that fade away on a tap.
 *
 * - Swipe left/right (or the arrows, on screen or keyboard) to step through.
 * - Swipe down, press Escape, tap X or use the phone's back button to close.
 * - Download gives the 2000px JPEG, free to anyone: people like finding
 *   themselves in the room.
 */
// How the main gallery's photos are served: WebP + JPEG ladders, and a 2000px
// JPEG download from this site.
const GALLERY_MEDIA = {
  src: (p) => photoSrc(p, 1200),
  webpSrcSet: (p) => webpSrcSet(p),
  jpgSrcSet: (p) => photoSrcSet(p, "jpg"),
  download: (p) => ({ href: photoSrc(p, 2000), name: `antara-${p.file}.jpg` }),
};

export function Lightbox({ images, index, onChange, onClose, media = GALLERY_MEDIA, extra = null }) {
  const photo = images[index];
  const closeRef = React.useRef(null);
  const [chrome, setChrome] = React.useState(true);
  const step = React.useCallback(
    (d) => onChange((i) => (i + d + images.length) % images.length),
    [onChange, images.length]
  );
  // The back button closes the viewer instead of leaving the page. One history
  // entry per opening (onClose is read through a ref, so re-renders while
  // stepping through photos never push another).
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const close = React.useCallback(() => {
    if (window.history.state && window.history.state.galleryLightbox) window.history.back();
    else onCloseRef.current();
  }, []);
  React.useEffect(() => {
    window.history.pushState({ ...(window.history.state || {}), galleryLightbox: true }, "");
    const onPop = () => onCloseRef.current();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (closeRef.current) closeRef.current.focus({ preventScroll: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [close, step]);
  // Warm the neighbours so a swipe lands on a photo that is already there.
  React.useEffect(() => {
    [1, -1].forEach((d) => {
      const n = images[(index + d + images.length) % images.length];
      if (n) new Image().src = media.src(n);
    });
  }, [index, images, media]);
  // A swipe must never also count as the tap that hides the controls.
  const dragging = React.useRef(false);
  const onDragEnd = (_, { offset, velocity }) => {
    setTimeout(() => { dragging.current = false; }, 80);
    const sideways = Math.abs(offset.x) > Math.abs(offset.y);
    if (sideways && (Math.abs(offset.x) > 70 || Math.abs(velocity.x) > 500)) step(offset.x < 0 ? 1 : -1);
    else if (!sideways && (offset.y > 120 || velocity.y > 700)) close();
  };
  const fade = `transition-opacity duration-300 ${chrome ? "opacity-100" : "opacity-0 pointer-events-none"}`;
  const btn = "w-11 h-11 rounded-full flex items-center justify-center bg-white/10 backdrop-blur-md text-[#FBF6EE] hover:bg-white/20 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E5C880]";
  const edition = photo.edition && (photo.edition.title ? `${photo.edition.title} · ${photo.edition.cityShort}` : photo.edition.label);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[70] overflow-hidden bg-[#0B0806] select-none"
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      data-testid="gallery-lightbox"
    >
      {/* The photo's own colours, blown up and blurred, as the room's light. */}
      <div
        key={`glow-${photo.file}`}
        aria-hidden="true"
        className="absolute inset-[-10%] bg-cover bg-center opacity-60 transition-[background-image] duration-500"
        style={{ backgroundImage: `url(${photo.lqip})`, filter: "blur(48px) saturate(1.3)" }}
      />
      <div aria-hidden="true" className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(11,8,6,0.15) 0%, rgba(11,8,6,0.75) 70%, rgba(11,8,6,0.92) 100%)" }} />

      <motion.div
        key={photo.file}
        className="absolute inset-0 flex items-center justify-center cursor-grab active:cursor-grabbing"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        drag
        dragDirectionLock
        dragSnapToOrigin
        dragElastic={0.6}
        onDragStart={() => { dragging.current = true; }}
        onDragEnd={onDragEnd}
        onTap={() => { if (!dragging.current) setChrome((c) => !c); }}
      >
        <picture>
          {media.webpSrcSet && <source type="image/webp" srcSet={media.webpSrcSet(photo)} sizes="100vw" />}
          <img
            src={media.src(photo)}
            srcSet={media.jpgSrcSet ? media.jpgSrcSet(photo) : undefined}
            sizes="100vw"
            alt={photo.alt}
            width={photo.width}
            height={photo.height}
            draggable={false}
            className="block max-w-[100vw] max-h-[100dvh] w-auto h-auto object-contain shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
            style={{ backgroundImage: `url(${photo.lqip})`, backgroundSize: "cover" }}
          />
        </picture>
      </motion.div>

      {/* Top: where you are, and the way out. */}
      <div className={`absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 px-4 pt-4 pb-10 bg-gradient-to-b from-black/60 to-transparent ${fade}`}>
        <p className="font-body text-sm text-[#FBF6EE]/90 tabular-nums" data-testid="gallery-counter">
          {index + 1} / {images.length}
          {edition && <span className="ml-2 text-[#E5C880]/90">· {edition}</span>}
        </p>
        <button ref={closeRef} type="button" className={btn} onClick={close} aria-label="Close">
          <X size={20} />
        </button>
      </div>

      {images.length > 1 && (
        <>
          <button type="button" className={`${btn} absolute z-10 left-3 top-1/2 -translate-y-1/2 ${fade}`} onClick={() => step(-1)} aria-label="Previous photograph">
            <ChevronLeft size={22} />
          </button>
          <button type="button" className={`${btn} absolute z-10 right-3 top-1/2 -translate-y-1/2 ${fade}`} onClick={() => step(1)} aria-label="Next photograph">
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {/* Bottom: what you are looking at, and keep it. */}
      <div className={`absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-4 px-4 pt-12 pb-[max(1rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-black/70 to-transparent ${fade}`}>
        <div className="min-w-0 max-w-xl">
          <p className="font-body text-sm leading-snug text-[#FBF6EE]/85 line-clamp-2">{photo.alt}</p>
          {extra && extra(photo)}
        </div>
        <a
          href={media.download(photo).href}
          download={media.download(photo).name || ""}
          className="shrink-0 inline-flex items-center gap-2 rounded-full px-4 h-11 bg-white/10 backdrop-blur-md text-[#FBF6EE] font-body text-sm hover:bg-white/20 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E5C880]"
          data-testid="gallery-download"
        >
          <Download size={17} aria-hidden="true" /> Download
        </a>
      </div>
    </motion.div>
  );
}

/* ========== 14. FAQ ========== */
const FAQS = [
  { q: "What actually happens at a bhajan jamming evening?", a: "A live band holds the drone and the groove while the whole hall sings the refrains. Two hours move from quiet, grounding bhajans into full-throated collective singing and back to stillness. There is no spectating — the audience is the choir." },
  { q: "Is the experience suitable for all ages?", a: "Antara is open to all ages and draws a multi-generational crowd. Children are welcome with a parent; the format is contemplative, so very young children may find the quieter passages long." },
  { q: "Is there a dress code?", a: "No formal dress code. Comfortable, traditional, or smart-casual attire that lets you sit, sway, and sing freely. Many guests choose kurtas or flowing fabrics in warm tones." },
  { q: "Do I need to know the bhajans, or be able to sing?", a: "No to both. The refrains are simple and repeat, the band leads them, and the words are easy to pick up within a line or two. Nobody is listening to you individually — that is rather the point of a room singing together." },
  { q: "Is bhajan jamming the same as bhajan clubbing?", a: "Close cousins. \"Bhajan clubbing\" is what many people now call nights where a live band plays bhajans and the crowd sings and dances along. Antara's bhajan jamming has the same joy — a live band and a whole room singing devotional songs together — staged in an auditorium or open-air venue, moving from quiet bhajans to full-voiced ones and back to stillness." },
  { q: "Can I bring Antara to my city, my event or my brand?", a: "Yes — that's how new editions happen. Add your city with the \"Bring Antara to your city\" form on the home page. For a wedding function, a sangeet, a corporate evening, a housewarming or a temple gathering, use the Host Antara page; brands and venues can write to us through the sponsorship form." },
];

// The two dated answers come from src/data/shows.js, so they can never
// promise a show that has already happened. With nothing ahead they fall back
// to the interest list.
function showFaqs(shows) {
  if (!shows.length) {
    return [
      { q: "When and where is the next Antara?", a: "Antara has played in Gurgaon, Delhi and Chandigarh — and the October 2026 editions in Delhi and Chandigarh both sold out. The next city and date go to the interest list first: tell us where you are and we'll let you know when Antara comes to you." },
      { q: "How do I get tickets?", a: "Nothing is on sale right now. When the next date is announced, the interest list hears first, along with the venue, the timings and the booking link." },
    ];
  }
  const list = shows
    .map((s) => `${s.city}: ${fullDate(s)}, ${s.venue}${timeLines(s).length ? ` (${timeLines(s).join(", ")})` : ""}`)
    .join("; ");
  return [
    { q: "When and where is the next Antara?", a: `${PRESENTER} presents ${EVENT_TITLE}, bhajan jamming featuring ${FEATURING} with Antara. ${list}.` },
    { q: "How do I get tickets?", a: `Tickets are on BookMyShow. Use the Book Tickets button for your city in the Upcoming Shows section on the home page — ${shows.map((s) => s.city).join(" and ")} each have their own listing.` },
  ];
}

export function faqItems(shows) {
  return [...showFaqs(shows), ...FAQS];
}

export function FAQ({ preview = false }) {
  const all = faqItems(useUpcomingShows());
  const items = preview ? all.slice(0, 4) : all;
  return (
    <section className="bg-ivory py-24 md:py-32 px-6" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <div className="text-center">
            <Eyebrow>Questions</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">Before you arrive</h2>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12 space-y-3" data-testid="faq-accordion">
            {items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-gold/25 bg-ivory-2 px-5">
                <AccordionTrigger className="font-display text-lg text-ink hover:no-underline" data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm text-ink-2/85 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        {preview && (
          <Reveal delay={0.15}>
            <div className="mt-10 text-center">
              <Link to={ROUTES.faq.path} className="inline-flex items-center gap-2 font-body text-sm tracking-wide text-amber hover:text-gold transition" data-testid="faq-see-all">
                All questions answered <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* ========== 15. Closing CTA ========== */
export function ClosingCTA() {
  return (
    <section className="py-24 md:py-32 px-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #FBF6EE 0%, #F2EAD9 50%, #E8D9C5 100%)" }} data-testid="closing-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Eyebrow>Build it with us</Eyebrow>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-ink leading-tight">Let's Build<br /><span className="gold-grad">This.</span></h2>
          <p className="mt-6 font-serif-italic text-xl text-amber">Be part of something that stays.</p>
          <p className="mt-6 font-body text-base text-ink-2/85 leading-relaxed max-w-lg">
            Antara is not just an event. It is a growing cultural format. Let's co-create something meaningful.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppClick} className="btn-primary" data-testid="closing-notify-btn">
              <Bell size={15} /> Notify Me — Next Edition
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppClick} className="btn-outline" data-testid="closing-whatsapp-btn">
              <MessageCircle size={15} /> Talk to us
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="relative">
            <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-gold/30 via-amber/15 to-transparent blur-2xl" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] border border-gold/30 shadow-[0_30px_80px_-20px_rgba(184,118,62,0.45)] bg-gradient-to-br from-[#FBF6EE] to-[#E8D9C5]">
              <img src={CLOSING_PHOTO} alt="Antara harmonium gathering" width="800" height="1000" loading="lazy" decoding="async" className="w-full h-full object-contain" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* Additional needed: ArrowRight import for above */

/* ========== 16. Footer ========== */
export function Footer() {
  const pillars = ["Story Driven", "Soulful Experience", "Rooted in Kashi", "Sacred Modernity", "Community & Connection", "Exclusive Journeys"];
  return (
    <footer className="bg-ivory pt-20 pb-10 px-6 relative overflow-hidden" data-testid="footer">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="Antara" width="56" height="56" className="h-14 w-14 object-contain" />
          <p className="mt-3 font-serif-italic text-sm text-amber">
            Presented by{" "}
            <a href={KASHI_SITE} target="_blank" rel="noopener" className="text-gold underline-offset-4 hover:underline transition" data-testid="footer-kashi-link">Kashi with Anshu</a>
          </p>
        </div>
        <LotusDivider />
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 max-w-3xl mx-auto">
          {pillars.map((p) => (
            <span key={p} className="eyebrow text-amber/70">{p}</span>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-center gap-5">
          <div className="flex items-center gap-5">
            <a href={ANTARA_IG} target="_blank" rel="noopener noreferrer" aria-label="Antara Instagram" className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold/10 transition" data-testid="footer-antara-ig">
              <Instagram size={18} strokeWidth={1.3} />
            </a>
            <a href={KASHI_IG} target="_blank" rel="noopener noreferrer" aria-label="Kashi with Anshu Instagram" className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold/10 transition" data-testid="footer-kashi-ig">
              <Instagram size={18} strokeWidth={1.3} />
            </a>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 font-body text-sm text-ink-2/80">
            <a href="mailto:hello@kashiwithanshu.com" className="flex items-center gap-2 hover:text-gold transition"><Mail size={14} />hello@kashiwithanshu.com</a>
            <a href="tel:+919355322133" className="flex items-center gap-2 hover:text-gold transition"><Phone size={14} />+91 93553 22133</a>
          </div>
        </div>
      </div>
      {/* Ganga ghat silhouette strip */}
      <div className="mt-14 relative h-20 overflow-hidden">
        <svg viewBox="0 0 1200 80" className="absolute bottom-0 left-0 right-0 w-full h-full" preserveAspectRatio="none" aria-hidden>
          <path d="M0,80 L0,52 L40,55 L60,38 L70,48 L100,42 L130,30 L160,35 L180,22 L210,28 L240,40 L280,32 L320,38 L350,25 L380,30 L410,18 L450,28 L490,35 L520,30 L560,40 L600,22 L640,28 L680,35 L720,28 L760,38 L800,30 L830,22 L870,30 L910,38 L950,30 L990,35 L1030,28 L1070,32 L1100,40 L1140,32 L1170,38 L1200,30 L1200,80 Z" fill="#9B8B7A" opacity="0.3" />
          <path d="M0,80 L0,62 L60,58 L120,68 L200,58 L280,65 L360,55 L440,62 L520,55 L600,65 L680,58 L760,62 L840,55 L920,65 L1000,58 L1080,62 L1160,58 L1200,65 L1200,80 Z" fill="#9B8B7A" opacity="0.5" />
        </svg>
      </div>
      <p className="text-center mt-4 font-body text-xs text-ink-2/60 tracking-wide">
        © 2026 Antara · Curated by Kashi with Anshu · bhajanjamming.com
      </p>
    </footer>
  );
}
